import {
    deviceConfigurationService,
    getReferralEnabled
} from '@/services/deviceConfigurationService'
import { HttpService } from '@/services/httpService'
import {
    ClaimReferralRequest,
    ClaimReferralResponse,
    ExchangeReferralRewardVoucherRequest,
    ExchangeReferralRewardVoucherResponse,
    ReferralEligibilityResponse,
    ReferralReward,
    ShareReferralSmsRequest,
    ShareReferralSmsResponse
} from '@/types/pos/referral'

const isReferralEnabled = () =>
    getReferralEnabled(deviceConfigurationService.getConfiguration())

const assertReferralEnabled = () => {
    if (!isReferralEnabled()) {
        throw new Error('Referral is disabled by device configuration')
    }
}

const api = {
    claim: '/common/referral/claim',
    shareSms: '/common/referral/share-sms',
    exchangeRewardVoucher: '/common/referral/reward/voucher',
    eligibility:
        '/common/voucher/{voucherCode}/vendor/{walletId}/{identifier}/referral-eligibility'
}

const DEFAULT_REFERRED_REWARD_AMOUNT = 200

const checkVoucherReferralEligibility = async (request: {
    voucherCode: string
    walletId: number
    identifier: string
}) => {
    if (!isReferralEnabled()) {
        return { canClaimReferralCode: false } satisfies ReferralEligibilityResponse
    }

    return HttpService.Post<ReferralEligibilityResponse>(
        api.eligibility
            .replace('{voucherCode}', request.voucherCode)
            .replace('{walletId}', String(request.walletId))
            .replace('{identifier}', request.identifier),
        undefined
    )
}

const claimReferralCode = async (request: ClaimReferralRequest) => {
    assertReferralEnabled()
    return HttpService.Post<ClaimReferralResponse>(api.claim, request)
}

const exchangeRewardVoucher = async (
    request: ExchangeReferralRewardVoucherRequest
) => {
    assertReferralEnabled()
    return HttpService.Post<ExchangeReferralRewardVoucherResponse>(
        api.exchangeRewardVoucher,
        request
    )
}

const shareReferralBySms = async (request: ShareReferralSmsRequest) => {
    assertReferralEnabled()
    return HttpService.Post<ShareReferralSmsResponse>(api.shareSms, request)
}

const getReferredRewardAmount = (
    rewards: ReferralReward[] | undefined
): number => {
    const referred = rewards?.find(
        (reward) =>
            reward.beneficiaryRole === 'REFERRED' &&
            typeof reward.amount === 'number'
    )
    return referred?.amount ?? DEFAULT_REFERRED_REWARD_AMOUNT
}

export const referralService = {
    checkVoucherReferralEligibility,
    claimReferralCode,
    exchangeRewardVoucher,
    shareReferralBySms,
    getReferredRewardAmount,
    DEFAULT_REFERRED_REWARD_AMOUNT
}
